import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Maximize, Minus, Plus, X } from "lucide-react";

/**
 * Full-screen view with zoom and pan, for diagrams and pictures in the chat.
 *
 * - Scroll wheel or + / - zoom (towards the pointer for the wheel), drag pans.
 * - "Fit" (or 0) returns to the whole picture on screen.
 * - Escape, the X, or a click on the dark backdrop closes it, and focus goes
 *   back to the button that opened it.
 *
 * Wheel zoom only applies inside this viewer. Inline in a message the wheel
 * scrolls the chat as normal, which is why zoom lives here and not in place.
 */

const MIN_SCALE = 0.25;
const MAX_SCALE = 8;
const STEP = 1.25;

export function ZoomViewer({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const stageRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  // Set once a drag actually moves, so releasing a pan over the backdrop does
  // not count as a click that closes the viewer.
  const moved = useRef(false);

  const fit = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const zoomBy = useCallback((factor: number) => {
    setScale((s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s * factor)));
  }, []);

  // Start from "fit" each time, focus the close button, and hand focus back
  // to whatever opened the viewer when it closes.
  useEffect(() => {
    if (!open) return;
    fit();
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => opener?.focus?.();
  }, [open, fit]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      } else if (e.key === "+" || e.key === "=") {
        zoomBy(STEP);
      } else if (e.key === "-" || e.key === "_") {
        zoomBy(1 / STEP);
      } else if (e.key === "0") {
        fit();
      }
    };
    // Capture phase, so Escape closes the viewer before anything underneath
    // (the settings popup, the files panel) reacts to it.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose, zoomBy, fit]);

  // Wheel zoom towards the pointer. Registered by hand because React's wheel
  // listener is passive and cannot stop the page scrolling.
  useEffect(() => {
    const stage = stageRef.current;
    if (!open || !stage) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = stage.getBoundingClientRect();
      const px = e.clientX - rect.left - rect.width / 2;
      const py = e.clientY - rect.top - rect.height / 2;
      const factor = e.deltaY < 0 ? STEP : 1 / STEP;
      setScale((s) => {
        const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s * factor));
        const k = next / s;
        setOffset((o) => ({ x: px - (px - o.x) * k, y: py - (py - o.y) * k }));
        return next;
      });
    };
    stage.addEventListener("wheel", onWheel, { passive: false });
    return () => stage.removeEventListener("wheel", onWheel);
  }, [open]);

  if (!open) return null;

  const button =
    "flex h-8 w-8 items-center justify-center rounded-md text-white/80 transition-colors hover:bg-white/10 hover:text-white";

  return createPortal(
    <div className="fixed inset-0 z-[200] flex flex-col bg-black/85" role="dialog" aria-modal="true" aria-label={title}>
      <div className="flex shrink-0 items-center justify-between px-4 py-2 text-white">
        <span className="truncate text-sm font-medium">{title}</span>
        <div className="flex items-center gap-1">
          <button type="button" className={button} onClick={() => zoomBy(1 / STEP)} aria-label="Zoom out" title="Zoom out (-)">
            <Minus size={16} />
          </button>
          <span className="w-12 text-center text-xs tabular-nums text-white/80">{Math.round(scale * 100)}%</span>
          <button type="button" className={button} onClick={() => zoomBy(STEP)} aria-label="Zoom in" title="Zoom in (+)">
            <Plus size={16} />
          </button>
          <button type="button" className={button} onClick={fit} aria-label="Fit to screen" title="Fit to screen (0)">
            <Maximize size={15} />
          </button>
          <button ref={closeRef} type="button" className={button} onClick={onClose} aria-label="Close full screen" title="Close (Esc)">
            <X size={17} />
          </button>
        </div>
      </div>
      <div
        ref={stageRef}
        className="relative flex min-h-0 flex-1 cursor-grab items-center justify-center overflow-hidden active:cursor-grabbing"
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
          moved.current = false;
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d) return;
          if (Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 3) moved.current = true;
          setOffset({ x: d.ox + e.clientX - d.x, y: d.oy + e.clientY - d.y });
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onClick={(e) => {
          // A click on the backdrop itself (not the picture, not after a drag)
          // closes the viewer.
          if (moved.current) {
            moved.current = false;
            return;
          }
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="select-none"
          style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transformOrigin: "center" }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * A picture that opens in the full-screen viewer when clicked. Used for images
 * in replies and for image and SVG previews in the files panel.
 */
export function ZoomableImage({
  src,
  alt,
  className,
  buttonClassName,
  onError,
}: {
  src: string;
  alt: string;
  className?: string;
  buttonClassName?: string;
  /** The picture failed to load; lets the caller show something better. */
  onError?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={buttonClassName ?? "block max-h-full max-w-full cursor-zoom-in"}
        aria-label={alt ? `View ${alt} full screen` : "View image full screen"}
        title="Click to view full screen"
      >
        <img src={src} alt={alt} className={className} onError={onError} />
      </button>
      <ZoomViewer open={open} onClose={() => setOpen(false)} title={alt || "Image"}>
        <img src={src} alt={alt} className="max-h-[80vh] max-w-[90vw] object-contain" draggable={false} />
      </ZoomViewer>
    </>
  );
}
